import React, { useState, useEffect, useCallback } from 'react';
import SeoPageComponent from '../../components/SeoPageComponent/SeoPageComponent';
import './Projects.css';
import { fetchProjects } from '../../services/projectService';
import logo from '../../assets/icon.png';
import Breadcrumbs from '../../components/Breadcrumbs';
import ListLoadingHolder from '../../components/LoadingHolders/ListLoadingHolder';
import ProjectCard from '../../components/ProjectCard/ProjectCard';

const romanianCounties = [
  'RO111 Bihor', 'RO112 Bistrita-Nasaud', 'RO113 Cluj', 'RO114 Maramures', 'RO115 Satu Mare', 'RO116 Salaj',
  'RO121 Alba', 'RO122 Brasov', 'RO123 Covasna', 'RO124 Harghita', 'RO125 Mures', 'RO126 Sibiu',
  'RO211 Bacau', 'RO212 Botosani', 'RO213 Iasi', 'RO214 Neamt', 'RO215 Suceava', 'RO216 Vaslui',
  'RO221 Braila', 'RO222 Buzau', 'RO223 Constanta', 'RO224 Galati', 'RO225 Tulcea', 'RO226 Vrancea',
  'RO311 Arges', 'RO312 Calarasi', 'RO313 Dambovita', 'RO314 Giurgiu', 'RO315 Ialomita', 'RO316 Prahova', 'RO317 Teleorman',
  'RO321 Bucuresti', 'RO322 Ilfov',
  'RO411 Dolj', 'RO412 Gorj', 'RO413 Mehedinti', 'RO414 Olt', 'RO415 Valcea',
  'RO421 Arad', 'RO422 Caras-Severin', 'RO423 Hunedoara', 'RO424 Timis',
];

const filterLabels = {
  projectTitle: {
    label: 'Denumire Proiect',
    placeholder: 'Introduceti titlul proiectului',
  },
  authorityName: {
    label: 'Autoritate contractanta',
    placeholder: 'Introduceti numele autoritatii',
  },
  procedureTitle: {
    label: 'Denumire procedura',
    placeholder: 'Introduceti o procedura din proiect',
  },
  startDateFrom: { label: 'Perioada inceput', placeholder: 'Data start' },
  startDateTo: { label: 'Perioada sfarsit', placeholder: 'Data sfarsit' },
  county: { label: 'Judet', placeholder: '', type: 'select', options: [
    { value: '', label: 'Toate' },
    ...romanianCounties.map(c => ({ value: c, label: c })),
  ]},
  contractTypeName: { label: 'Tip contract', placeholder: '', type: 'select', options: [
    { value: '', label: 'Toate' },
    { value: 'Lucrari', label: 'Lucrari' },
    { value: 'Servicii', label: 'Servicii' },
  ]},
  estimatedValueMin: { label: 'Valoare min (RON)', placeholder: 'Min', type: 'number' },
  estimatedValueMax: { label: 'Valoare max (RON)', placeholder: 'Max', type: 'number' },
  fundingSource: { label: 'Sursa finantare', placeholder: 'Ex: Fonduri bugetare' },
  cpvCode: { label: 'Cod CPV', placeholder: 'Ex: 45200000' },
  minProcedureCount: { label: 'Min. proceduri', placeholder: 'Ex: 3', type: 'number' },
  projectStatus: { label: 'Status proiect', placeholder: '', type: 'select', options: [
    { value: '', label: 'Toate' },
    { value: 'Anunt de intentie', label: 'Anunt de intentie' },
    { value: 'In planificare', label: 'In planificare' },
    { value: 'Proiectare in licitatie', label: 'Proiectare in licitatie' },
    { value: 'Executie in licitatie', label: 'Executie in licitatie' },
    { value: 'Supervizare in licitatie', label: 'Supervizare in licitatie' },
    { value: 'Partial atribuit', label: 'Partial atribuit' },
    { value: 'In desfasurare', label: 'In desfasurare' },
    { value: 'In licitatie', label: 'In licitatie' },
  ]},
  projectPhase: { label: 'Faza proiect', placeholder: '', type: 'select', options: [
    { value: '', label: 'Toate' },
    { value: 'Intentie', label: 'Intentie' },
    { value: 'Planificare', label: 'Planificare' },
    { value: 'Proiectare', label: 'Proiectare' },
    { value: 'Executie', label: 'Executie' },
    { value: 'Dirigentie', label: 'Dirigentie' },
    { value: 'Supervizare', label: 'Supervizare' },
    { value: 'Dotare', label: 'Dotare' },
    { value: 'Furnizare', label: 'Furnizare' },
    { value: 'Atribuire', label: 'Atribuire' },
  ]},
  hasWinner: { label: 'Are castigator', placeholder: '', type: 'select', options: [
    { value: '', label: 'Toate' },
    { value: 'true', label: 'Da' },
  ]},
};

const Projects = () => {
  // SEO prerender setup
  useEffect(() => {
    if (typeof window !== 'undefined') window.prerenderReady = false;
  }, []);

  const [filters, setFilters] = useState({
    projectTitle: '',
    authorityName: '',
    procedureTitle: '',
    startDateFrom: '',
    startDateTo: '',
    county: '',
    contractTypeName: '',
    estimatedValueMin: '',
    estimatedValueMax: '',
    fundingSource: '',
    cpvCode: '',
    minProcedureCount: '',
    projectStatus: '',
    projectPhase: '',
    hasWinner: '',
    pageIndex: 0,
    pageSize: 10,
  });

  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [errors, setErrors] = useState([]);

  const validateFilters = useCallback(() => {
    const validationErrors = [];
    setErrors(validationErrors);
    return validationErrors.length === 0;
  }, []);

  const handleFilter = useCallback(
    async (pageIndex = filters.pageIndex) => {
      if (!validateFilters()) return;
      setLoading(true);
      try {
        const data = await fetchProjects({ ...filters, pageIndex });
        setResults(data.results);
        setTotalResults(data.totalResults);
      } catch (error) {
        console.error('Eroare la căutarea proiectelor:', error);
      } finally {
        setLoading(false);
      }
    },
    [filters, validateFilters],
  );

  useEffect(() => {
    handleFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set prerenderReady when loading finishes
  useEffect(() => {
    if (!loading) {
      if (typeof window !== 'undefined') window.prerenderReady = true;
    }
  }, [loading]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    const resetFilters = {
      projectTitle: '',
      authorityName: '',
      procedureTitle: '',
      startDateFrom: '',
      startDateTo: '',
      county: '',
      contractTypeName: '',
      estimatedValueMin: '',
      estimatedValueMax: '',
      fundingSource: '',
      cpvCode: '',
      minProcedureCount: '',
      projectStatus: '',
      projectPhase: '',
      hasWinner: '',
      pageIndex: 0,
      pageSize: 10,
    };
    setFilters(resetFilters);
    setResults([]);
    setTotalResults(0);
    setErrors([]);
    handleFilter(0);
  };

  const handlePageChange = (newPageIndex) => {
    setFilters((prev) => ({ ...prev, pageIndex: newPageIndex }));
    handleFilter(newPageIndex);
  };

  const handlePageSizeChange = (e) => {
    const newPageSize = parseInt(e.target.value);
    setFilters((prev) => ({ ...prev, pageSize: newPageSize, pageIndex: 0 }));
    handleFilter(0);
  };

  const breadcrumbLinks = [{ href: '/', label: 'Home' }, { label: 'Proiecte' }];
  const pageTitle = `Proiecte - ${import.meta.env.VITE_NAME}`;
  const pageDescription = `Explorează proiectele publice structurate pe proceduri. Filtrare după autoritate, denumire și perioadă.`;
  const pageKeywords = `proiecte publice, achiziții, proceduri, autoritate, CPV`;
  const pageUrl = `${window.location.origin}/proiecte`;

  return (
    <>
      <SeoPageComponent
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        keywords={pageKeywords}
        pageUrl={pageUrl}
        logoUrl={logo}
        canonicalUrl={pageUrl}
      />
      <section className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-green-100 py-12">
        <div className="container max-w-5xl mx-auto">
          <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />
          <h1 className="text-4xl md:text-5xl font-extrabold text-indigo-700 mb-4 text-center">
            Proiecte publice structurate, la un click distanță
          </h1>
          <p className="text-gray-600 text-center mb-10 text-lg max-w-2xl mx-auto">
            Descoperă rapid proiecte relevante, filtrează inteligent și vezi
            detalii complete pentru fiecare oportunitate din construcții.
          </p>

          <div className="bg-white rounded-2xl shadow-2xl p-8 border-t-8 border-indigo-500 mb-10 animate-fade-in">
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="w-full bg-gradient-to-r from-indigo-500 to-green-500 text-white px-4 py-3 rounded-xl font-bold text-lg hover:from-indigo-600 hover:to-green-600 transition-all flex justify-between items-center mb-4"
            >
              {isFilterOpen ? 'Ascunde filtrele ▲' : 'Afișează filtrele ▼'}
            </button>
            {isFilterOpen && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
                {Object.keys(filters)
                  .filter((k) => !['pageIndex', 'pageSize'].includes(k))
                  .map((filter) => {
                    const config = filterLabels[filter];
                    if (config?.type === 'select') {
                      return (
                        <div key={filter}>
                          <label className="block text-gray-700 font-semibold mb-1">
                            {config.label}
                          </label>
                          <select
                            name={filter}
                            value={filters[filter]}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none bg-white"
                          >
                            {config.options.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      );
                    }
                    return (
                      <div key={filter}>
                        <label className="block text-gray-700 font-semibold mb-1">
                          {config?.label || filter}
                        </label>
                        <input
                          type={config?.type === 'number' ? 'number' : filter.includes('Date') ? 'date' : 'text'}
                          name={filter}
                          value={filters[filter]}
                          onChange={handleInputChange}
                          placeholder={config?.placeholder || ''}
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                        />
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {errors.length > 0 && (
            <div className="mb-6 bg-red-100 border border-red-300 text-red-700 p-4 rounded-xl text-center">
              <ul className="list-disc ml-5 text-sm">
                {errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 animate-fade-in">
            <div className="flex items-center justify-end space-x-4 border-b pb-5 mb-8">
              <button
                onClick={() => handleFilter()}
                className="bg-gradient-to-r from-indigo-500 to-green-500 text-white px-6 py-2 rounded-xl font-bold hover:from-indigo-600 hover:to-green-600 transition-all"
              >
                Filtrează
              </button>
              <button
                onClick={handleReset}
                className="bg-red-100 hover:bg-red-300 text-gray-700 px-6 py-2 rounded-xl font-bold hover:text-white duration-300"
              >
                Resetează
              </button>
            </div>

            <div className="mt-8 space-y-6">
              {loading ? (
                <ListLoadingHolder />
              ) : results.length === 0 ? (
                <p className="text-center text-gray-600">
                  Niciun proiect găsit.
                </p>
              ) : (
                results.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))
              )}
            </div>

            {totalResults > filters.pageSize && (
              <div className="mt-8 flex flex-wrap justify-center items-center gap-2 bg-indigo-50 p-4 rounded-xl">
                <button
                  onClick={() => handlePageChange(0)}
                  disabled={filters.pageIndex === 0}
                  className="px-3 py-2 rounded-lg font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Prima
                </button>
                <button
                  onClick={() => handlePageChange(filters.pageIndex - 1)}
                  disabled={filters.pageIndex === 0}
                  className="px-3 py-2 rounded-lg font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  &laquo;
                </button>
                {Array.from(
                  {
                    length: Math.min(
                      5,
                      Math.ceil(totalResults / filters.pageSize),
                    ),
                  },
                  (_, i) => {
                    const totalPages = Math.ceil(
                      totalResults / filters.pageSize,
                    );
                    let startPage = Math.max(
                      0,
                      Math.min(filters.pageIndex - 2, totalPages - 5),
                    );
                    let page = startPage + i;
                    if (page < 0 || page >= totalPages) return null;
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 rounded-lg font-bold transition ${
                          filters.pageIndex === page
                            ? 'bg-indigo-500 text-white shadow-lg'
                            : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                        }`}
                      >
                        {page + 1}
                      </button>
                    );
                  },
                )}
                <button
                  onClick={() => handlePageChange(filters.pageIndex + 1)}
                  disabled={
                    filters.pageIndex >=
                    Math.ceil(totalResults / filters.pageSize) - 1
                  }
                  className="px-3 py-2 rounded-lg font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  &raquo;
                </button>
                <button
                  onClick={() =>
                    handlePageChange(
                      Math.ceil(totalResults / filters.pageSize) - 1,
                    )
                  }
                  disabled={
                    filters.pageIndex >=
                    Math.ceil(totalResults / filters.pageSize) - 1
                  }
                  className="px-3 py-2 rounded-lg font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ultima
                </button>
                <label className="ml-4 text-gray-700 text-sm flex items-center gap-2">
                  / pagină
                  <select
                    value={filters.pageSize}
                    onChange={handlePageSizeChange}
                    className="ml-1 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-400 outline-none bg-white"
                  >
                    {[10, 20, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
                <span className="ml-4 text-gray-500 text-sm">
                  Pagina{' '}
                  {Math.min(
                    filters.pageIndex + 1,
                    Math.max(1, Math.ceil(totalResults / filters.pageSize)),
                  )}{' '}
                  din {Math.ceil(totalResults / filters.pageSize)}
                </span>
                <span className="text-gray-500 text-sm">
                  ({totalResults}{' '}
                  {totalResults === 1 ? 'rezultat' : 'rezultate'})
                </span>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Projects;
